import { useSeedanceAuthStore } from '../store/seedanceAuthStore';
import { SEEDANCE_STORAGE_KEYS, setLocalStorageValue } from './seedanceStorage';

/**
 * 外部站点跳转到本应用时，可在 URL 上携带 `?settings=<encodeURIComponent(JSON)>`，
 * 例如：`http://host:8001/?settings={"apiKey":"...","userId":"1","safeSystemKey":"..."}#/workspace`
 *
 * 字段映射：
 * - apiKey        → 火山引擎 API Key（ss_key）
 * - userId        → 素材库 / 任务管理的用户 ID（ss_user_id）
 * - safeSystemKey → 系统访问令牌（ss_system_token）
 * - baseURL       → 暂未使用
 */
export interface IncomingSettings {
  apiKey?: string;
  baseURL?: string;
  userId?: string | number;
  safeSystemKey?: string;
}

const sanitizeApiKey = (v: string): string =>
  v.trim().replace(/^Bearer\s+/i, '').replace(/[^a-zA-Z0-9-]/g, '');

/**
 * 从 URL search 中解析 settings 参数。
 *
 * 不用 URLSearchParams：它遵循 form-urlencoded，会把 `+` 解析成空格，
 * 而 safeSystemKey 是 base64（含 `+`），必须用 decodeURIComponent 保留原值。
 */
export function readSettingsParamFromUrl(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): IncomingSettings | null {
  if (!search) return null;
  const match = search.match(/[?&]settings=([^&]*)/);
  if (!match) return null;
  let json: string;
  try {
    json = decodeURIComponent(match[1]);
  } catch {
    return null;
  }
  try {
    const parsed = JSON.parse(json);
    if (parsed && typeof parsed === 'object') return parsed as IncomingSettings;
  } catch {
    /* 非法 JSON，忽略 */
  }
  return null;
}

/** 从地址栏移除 settings 参数，避免敏感信息残留或被重复解析（保留其它参数与 hash 路由）。 */
function cleanSettingsParamFromUrl(): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('settings')) return;
    url.searchParams.delete('settings');
    const rest = url.searchParams.toString();
    window.history.replaceState(
      null,
      '',
      url.pathname + (rest ? `?${rest}` : '') + url.hash,
    );
  } catch {
    /* 地址栏清理失败不影响主流程 */
  }
}

/**
 * 解析 URL 上的 settings 参数并写入用户配置（store + localStorage 持久化）。
 *
 * @returns 若成功解析出 apiKey 则返回该 key（供调用方触发连接校验），否则返回 null。
 */
export function applyIncomingSettingsFromUrl(): { apiKey: string } | null {
  const settings = readSettingsParamFromUrl();
  if (!settings) return null;

  const auth = useSeedanceAuthStore.getState();
  let appliedApiKey = '';

  if (typeof settings.apiKey === 'string' && settings.apiKey.trim()) {
    const k = sanitizeApiKey(settings.apiKey);
    if (k) {
      appliedApiKey = k;
      auth.setApiKey(k);
      setLocalStorageValue(SEEDANCE_STORAGE_KEYS.apiKey, k);
    }
  }

  if (
    settings.userId !== undefined &&
    settings.userId !== null &&
    `${settings.userId}`.trim()
  ) {
    const uid = `${settings.userId}`.trim();
    auth.setConfigUserId(uid);
    setLocalStorageValue(SEEDANCE_STORAGE_KEYS.configUserId, uid);
  }

  if (typeof settings.safeSystemKey === 'string' && settings.safeSystemKey.trim()) {
    const token = settings.safeSystemKey.trim();
    auth.setSystemToken(token);
    setLocalStorageValue(SEEDANCE_STORAGE_KEYS.systemToken, token);
  }

  cleanSettingsParamFromUrl();

  return appliedApiKey ? { apiKey: appliedApiKey } : null;
}
